import moment from 'moment';
import async from 'async';
import groupBy from 'lodash/groupBy';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import sortBy from 'lodash/sortBy';

import { executeListQuery, executeQuery, getItem } from '../../../services/api/graphQlRepository';
import Auth from '../../../services/api/auth';
import AppointmentService from '../../Appointments/services/AppointmentService';
import { APPOINTMENT_PARTICIPANT_STATUS } from '../../Appointments/constants';
import each from 'lodash/each';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import values from 'lodash/values';

const getDays = function (page, timeslotToTimezone, next) {
    if (page && page.id && page.params && page.params.filter && page.params.filter.typeId) {
        const root = page.params.filter.typeId;
        executeQuery('getTimeslotsWithType', {
            type: root,
        }).then(timeslots => {
            if (!timeslots.length) {
                return next(null, []);
            }

            // timeslots = timeslots.map(session => {
            //     return timeslotToTimezone(session);
            // });

            next(null, sortBy(timeslots, ['start']));
        });
    } else {
        next('no programme page found or root is not configured');
    }
};

const mapNoGroupedToObject = (group, groupId) => {
    switch (groupId) {
        case 'nonLocated':
            return {
                group: {
                    id: 'noPlace',
                    name: 'No specified location',
                },
                items: sortBy(group[groupId], ['start']),
            };
        case 'nonClassified':
            return {
                group: {
                    id: 'noClassifier',
                    name: 'No specified classifier',
                },
                items: sortBy(group[groupId], ['start']),
            };
        case 'nonGrouped':
            return {
                group: {
                    id: 'noGroup',
                },
                items: sortBy(group[groupId], ['start']),
            };
        case 'other':
            return {
                group: {
                    id: 'other',
                    name: 'Other',
                },
                items: sortBy(group[groupId], ['start']),
            };
        default:
            return {
                group: {
                    id: 'noPlace',
                    name: 'No specified location',
                },
                items: sortBy(group[groupId], ['start']),
            };
    }
};

const groupByPlace = sessions => {
    return groupBy(sessions, session => {
        if (session) {
            if (session.objectClass === 'Appointment') {
                return 'other';
            }

            return session.locations && session.locations.length
                ? session.locations[0]._id
                : 'nonLocated';
        }

        return 'noSession';
    });
};

const groupByClassifier = (sessions, classifiers) => {
    // Group items by classifier id
    const group = classifiers.reduce((acc, cls) => {
        const sessionsForClassifier = sessions.filter(
            session =>
                session.classifications && session.classifications.find(c => c._id === cls.id),
        );

        // Remove the items that has been already grouped
        sessions = sessions.filter(
            session => !sessionsForClassifier.find(sfc => sfc.id === session.id),
        );

        acc[cls.id] = sessionsForClassifier;

        return acc;
    }, {});

    group.other = sessions.filter(session => session.objectClass === 'Appointment');
    sessions = sessions.filter(session => session.objectClass !== 'Appointment');
    group.nonClassified = sessions;

    return group;
};

const shouldDisplayAppointment = appointment =>
    appointment &&
    appointment.members &&
    appointment.members.length &&
    appointment.members[0].AppointmentGroup &&
    (appointment.members[0].AppointmentGroup.status === APPOINTMENT_PARTICIPANT_STATUS.WAITING ||
        appointment.members[0].AppointmentGroup.status === APPOINTMENT_PARTICIPANT_STATUS.ACCEPTED);

const getUserAppointmentsFromDay = async day => {
    if (!Auth.isUserAuthenticated()) {
        return [];
    }

    const user = Auth.getUser();
    const appointments = await AppointmentService.getAllAppointments(user.id);
    const appointmentsToDisplay = appointments.filter(shouldDisplayAppointment);
    const dayStart = moment.utc(day.start);
    const dayEnd = moment.utc(day.end);

    const filteredAppointments = appointmentsToDisplay.filter(appointment => {
        let appointmentStart = moment.utc(appointment.start);

        if (!appointmentStart.isValid()) {
            appointmentStart = moment.unix(appointment.start);
        }

        return appointmentStart.isSameOrAfter(dayStart) && appointmentStart.isSameOrBefore(dayEnd);
    });

    // Convert unix time to string format
    return filteredAppointments.map(app => ({
        ...app,
        start: moment.unix(app.start).utc().toString(),
        end: moment.unix(app.end).utc().toString(),
        type: 'appointment',
    }));
};

var getSessionsFromDay = async function (
    page,
    day,
    skipTimeframes,
    grouping,
    timeslotToTimezone,
    next,
) {
    let sessions = [];
    const { groupingOption, groupingValue } = grouping;

    const timeframes = await executeListQuery('findTimeslots', {
        parent: {
            eq: day.id,
        },
    });
    // Merge appointents (eureka entity) with other sessions
    const appointments = await getUserAppointmentsFromDay(day);
    sessions.push(...appointments);
    if (!timeframes.length) {
        return next('no timeframes found');
    }

    if (!skipTimeframes) {
        for (let index = 0; index < timeframes.length; index++) {
            const slots = await executeListQuery('findTimeslots', {
                parent: {
                    eq: timeframes[index]['id'],
                },
            });

            sessions.push(...slots);
        }
    } else {
        sessions.push(...timeframes);
    }

    sessions = sessions.map(session => {
        return timeslotToTimezone(session);
    });
    const classifierIdForFilter = page?.params?.filter?.classifierId;

    if (classifierIdForFilter) {
        sessions = sessions.filter(item =>
            item.classifications?.find(cls => cls._id === classifierIdForFilter),
        );
    }

    const result = [];
    const sortedSessionsByEnd = sessions.sort((a, b) => new Date(b.end) - new Date(a.end));
    const last = (sortedSessionsByEnd && sortedSessionsByEnd[0]) || maxBy(sessions, o => o.end);
    const first = minBy(sessions, o => o.start);

    const timerange = {
        start: first && moment.utc(first.start).hour(),
        end: last && moment.utc(last.end).hour() + 1,
    };

    if (timerange.start > timerange.end) {
        timerange.end = timerange.end + 24;
    }

    let classifiers = [];
    let group;
    if (groupingOption === 'byPlace') {
        group = groupByPlace(sessions);
    } else if (groupingOption === 'noGrouping') {
        group = { nonGrouped: sessions };
    } else if (groupingOption === 'byClassifier') {
        classifiers = await executeQuery('getClassifiersWithType', { type: groupingValue });
        group = groupByClassifier(sessions, classifiers);
    } else {
        // Default case: group by location
        group = groupByPlace(sessions);
    }
    const groups = Object.keys(group);

    async.each(
        groups,
        function (groupId, cb) {
            // if no group defined, return early
            if (
                groupId === 'nonLocated' ||
                groupId === 'nonClassified' ||
                groupId === 'nonGrouped' ||
                groupId === 'other'
            ) {
                const object = mapNoGroupedToObject(group, groupId);
                result.push(object);

                return cb();
            }

            if (groupingOption === 'byPlace' || !groupingOption) {
                getItem('places', groupId, (err, place) => {
                    const object = {
                        group: {
                            id: groupId || 'noPlace',
                            name: place && place.name ? place.name : 'No specified location',
                            orderingName: place && place.orderingName,
                        },
                        items: sortBy(group[groupId], ['start']),
                    };
                    result.push(object);

                    return cb(err);
                });
            } else if (groupingOption === 'byClassifier') {
                const classifier = classifiers.find(cl => cl.id === groupId);

                const object = {
                    group: {
                        id: groupId || 'noClassifier',
                        name:
                            classifier && classifier.name
                                ? classifier.name
                                : 'No specified classifier',
                        orderingName: classifier && classifier.orderingName,
                    },
                    items: sortBy(group[groupId], ['start']),
                };
                result.push(object);

                return cb(null);
            } else {
                return cb('Invalid grouping option');
            }
        },
        err => {
            next(
                err,
                sortBy(result, [
                    function (elem) {
                        return elem.group.orderingName;
                    },
                    function (elem) {
                        return elem.group.name;
                    },
                ]),
                timerange,
            );
        },
    );
};

function getFilterTypes(page, callback) {
    let mappedFilters = [];
    if (page && page.params && page.params.webappfilters) {
        async.eachSeries(
            page.params.webappfilters,
            function (filter, next) {
                if (!filter) {
                    return next();
                }

                getItem('types', filter, function (err, type) {
                    if (type && type.singular) {
                        mappedFilters.push({
                            name: type.singular,
                            type: filter,
                        });
                    }
                    next(err);
                });
            },
            function (err) {
                callback(err, mappedFilters);
            },
        );
    } else {
        callback();
    }
}

function getGroupingOptions(page, callback) {
    if (page && page.params && page.params.groupingOption) {
        callback(null, {
            groupingOption: page.params.groupingOption,
            groupingValue: page.params.groupingValue,
        });
    } else {
        callback(null, {});
    }
}

function filterProgram(filter, data, favorites, marked) {
    if (filter && data && data.length) {
        let result = [];

        // for each group, iterate its items
        each(data, function (locationItem) {
            let newItem = {};
            newItem.group = locationItem.group;
            newItem.items = [];

            // for each program item of a group
            each(locationItem.items, function (item) {
                let textMatch =
                        (filter.text &&
                            item.name.toLowerCase().includes(filter.text.toLowerCase())) ||
                        !filter.text ||
                        filter.text.length < 1,
                    filterMatch = false,
                    filterFavorite =
                        !favorites ||
                        find(favorites, function (f) {
                            return f.id === item.id && f.action === 'Add';
                        });
                if (!filterFavorite && marked && marked.length) {
                    filterFavorite = find(marked, function (m) {
                        return m === item.id;
                    });
                }

                if (!isEmpty(filter.filters)) {
                    // iterate the filter groups, and add if the filter is found and the text filter matches (or is missing)
                    each(values(filter.filters), function (filterGroup) {
                        // for each filter (classifier), check if it exist in the item classifications
                        each(filterGroup, function (filter) {
                            if (
                                filterFavorite &&
                                textMatch &&
                                !filterMatch &&
                                filter &&
                                find(item.classifications, function (i) {
                                    return i._id === filter.id;
                                })
                            ) {
                                newItem.items.push(item);
                                filterMatch = true;
                            }
                        });
                    });
                } else if (textMatch && filterFavorite) {
                    newItem.items.push(item);
                }
            });
            result.push(newItem);
        });

        return result;
    } else {
        return data;
    }
}

export { getDays, getSessionsFromDay, getFilterTypes, getGroupingOptions, filterProgram };
